import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import type { BaseNurseShift, DayItem, Nurse, NurseFullRole, NurseOvertimeMap, OvertimeBadgeType, ShiftStaffTimes, ShiftTime } from "../../utils/models";
import { findDuplicateTimeSlots } from "../../utils/utilFunctions";
import { hasSignificantShiftOverlap } from "../StaffTable/utils";
import { matchesContractSections } from "../team/config";

export function isDateRange(time: unknown): time is [DateTime, DateTime] {
	return Array.isArray(time) && time.length === 2 && DateTime.isDateTime(time[0]) && DateTime.isDateTime(time[1]);
}

type UseShiftFiltersParams = {
	dayItem: DayItem;
	date: DateTime;
	nurses: BaseNurseShift[];
	selectedNurse: Nurse | null;
	selectedLocations: string[];
	availableLocations: string[];
	showActual: boolean;
	shiftsForTimeFilters: BaseNurseShift[];
	selectedContractSections?: ReadonlySet<string>;
	otMap?: NurseOvertimeMap;
	otDateKey?: string;
};
export function useShiftFilters({
	dayItem,
	date,
	nurses,
	selectedNurse,
	selectedLocations,
	availableLocations,
	showActual,
	shiftsForTimeFilters,
	selectedContractSections,
	otMap,
	otDateKey,
}: UseShiftFiltersParams) {
	const { settings } = useUserSettingsContext();
	const { track } = useAnalytics();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);

	const adjustDate = useCallback(
		(d: DateTime): DateTime => {
			return d.set({ year: date.year, month: date.month, day: date.day });
		},
		[date],
	);

	const [selectedRoles, setSelectedRoles] = useState<NurseFullRole[] | "All">("All");
	const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
	const [selectedTimes, setSelectedTimes] = useState<ShiftTime[]>(["All"]);
	const [selectedOtBadgeTypes, setSelectedOtBadgeTypes] = useState<OvertimeBadgeType[] | "All">("All");
	// Timeline starts collapsed by default; the user opens it via the toggle.
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [sliderStart, setSliderStart] = useState<DateTime>(dayItem.earliestTime);
	const [sliderEnd, setSliderEnd] = useState<DateTime>(dayItem.latestTime);

	useEffect(() => {
		setSelectedTimes((prev) => {
			return prev.map((times) => {
				if (Array.isArray(times)) {
					const [t1, t2] = times;
					return [adjustDate(t1), adjustDate(t2)];
				}
				return times;
			});
		});

		setSliderStart(adjustDate);
		setSliderEnd(adjustDate);
	}, [adjustDate]);

	useEffect(() => {
		if (selectedTimes.length === 1 && selectedTimes[0] === "All") {
			setSliderStart(dayItem.earliestTime);
			setSliderEnd(dayItem.latestTime);
		}
	}, [dayItem, selectedTimes]);

	const filterStaff = useCallback(
		(staffList: BaseNurseShift[]): BaseNurseShift[] => {
			const hasSelectedLocations = selectedLocations.length > 0;
			const isFullDayRange = sliderStart.equals(dayItem.earliestTime) && sliderEnd.equals(dayItem.latestTime);

			// Does a [start, end] range fall within the currently-selected chip window?
			const matchesSelectedTime = (start: DateTime, end: DateTime): boolean => {
				if (settings.overlapShifts) {
					return start < sliderEnd.minus({ hours: 1 }) && end > sliderStart.plus({ hours: 1 });
				}
				return start.hasSame(sliderStart, "hour") && end.hasSame(sliderEnd, "hour");
			};

			return staffList.filter((nurse: BaseNurseShift) => {
				if (selectedNurse && nurse.nurseId !== selectedNurse.id) {
					return false;
				}
				if (jobManagementEnabled) {
					if (selectedJobs.length > 0 && (!nurse.jobId || !selectedJobs.includes(nurse.jobId))) {
						return false;
					}
				} else if (selectedRoles !== "All" && !selectedRoles.includes(nurse.role)) {
					return false;
				}
				if (selectedContractSections && !matchesContractSections(nurse.type, selectedContractSections)) {
					return false;
				}
				if (nurse.location && hasSelectedLocations && !selectedLocations.includes(nurse.location) && availableLocations.includes(nurse.location)) {
					return false;
				}
				if (selectedOtBadgeTypes !== "All" && otMap && otDateKey) {
					const nurseOtInfo = otMap[nurse.nurseId]?.[otDateKey];
					if (!nurseOtInfo || !selectedOtBadgeTypes.includes(nurseOtInfo.type)) {
						return false;
					}
				}
				if (isFullDayRange) {
					return true;
				}

				// Actuals are punch records that rarely line up exactly with a shift window. For people who
				// were scheduled, filter by their *scheduled* time for the day (so e.g. a 6:53am punch still
				// matches the 7a chip). For people who were not scheduled, keep them when their punch overlaps
				// the selected chip by at least 30% of the chip's duration.
				if (showActual) {
					const scheduledShifts = dayItem.shifts.filter((scheduled) => scheduled.nurseId === nurse.nurseId);
					if (scheduledShifts.length > 0) {
						return scheduledShifts.some((scheduled) => matchesSelectedTime(scheduled.startTime, scheduled.endTime));
					}
					return hasSignificantShiftOverlap({ startTime: sliderStart, endTime: sliderEnd }, { startTime: nurse.startTime, endTime: nurse.endTime });
				}

				return matchesSelectedTime(nurse.startTime, nurse.endTime);
			});
		},
		[
			selectedRoles,
			selectedJobs,
			jobManagementEnabled,
			selectedLocations,
			selectedNurse,
			availableLocations,
			sliderStart,
			sliderEnd,
			dayItem,
			settings,
			selectedContractSections,
			showActual,
			selectedOtBadgeTypes,
			otMap,
			otDateKey,
		],
	);

	const staff = useMemo<BaseNurseShift[]>(() => filterStaff(nurses), [nurses, filterStaff]);

	const shiftTimeFilters = useMemo<ShiftTime[]>(() => {
		const duplicateTimes = findDuplicateTimeSlots(shiftsForTimeFilters, dayItem.requests).map<[DateTime, DateTime]>((dup) => [dup.startTime, dup.endTime]);
		return ["All", ...duplicateTimes];
	}, [shiftsForTimeFilters, dayItem]);

	const onRoleTypeFilterChange = useCallback(
		(roles: NurseFullRole[] | "All"): void => {
			track("dayview_filter_role", { roles: JSON.stringify(roles), showActual });
			setSelectedRoles(roles);
		},
		[showActual, track],
	);

	const onJobTypeFilterChange = useCallback(
		(jobs: string[]): void => {
			track("dayview_filter_job", { jobs: JSON.stringify(jobs), showActual });
			setSelectedJobs(jobs);
		},
		[showActual, track],
	);

	const onShiftTimeFilterChange = useCallback(
		(shiftTimes: ShiftTime[]): void => {
			track("dayview_filter_times", { shiftTimes: JSON.stringify(shiftTimes), showActual });
			setSelectedTimes(shiftTimes);

			const hasAll = shiftTimes.some((time) => time === "All");
			if (hasAll) {
				setSliderStart(dayItem.earliestTime);
				setSliderEnd(dayItem.latestTime);
			} else if (shiftTimes.length > 0) {
				const timeRanges = shiftTimes.filter(isDateRange);

				if (timeRanges.length > 0) {
					const earliestStartInit = adjustDate(timeRanges[0][0]);
					const earliestStart = timeRanges.reduce<DateTime>((earliest, [start]) => (start < earliest ? start : earliest), earliestStartInit);

					const latestEndInit = adjustDate(timeRanges[0][1]);
					const latestEnd = timeRanges.reduce<DateTime>((latest, [, end]) => (end > latest ? end : latest), latestEndInit);

					setSliderStart(earliestStart);
					setSliderEnd(latestEnd);
				}
			} else {
				setSliderStart(dayItem.earliestTime);
				setSliderEnd(dayItem.latestTime);
			}
		},
		[dayItem, adjustDate, showActual, track],
	);

	const onShiftClick = useCallback((shiftTimes: ShiftStaffTimes): void => {
		const newTimeRange: [DateTime, DateTime] = [shiftTimes.startTime, shiftTimes.endTime];
		setSelectedTimes([newTimeRange]);
		setSliderStart(shiftTimes.startTime);
		setSliderEnd(shiftTimes.endTime);
		setSelectedRoles([shiftTimes.role]);
	}, []);

	const onOtBadgeFilterChange = useCallback(
		(types: OvertimeBadgeType[] | "All"): void => {
			track("dayview_filter_ot_badge", { types: JSON.stringify(types), showActual });
			setSelectedOtBadgeTypes(types);
		},
		[showActual, track],
	);

	return {
		adjustDate,
		selectedRoles,
		selectedJobs,
		selectedTimes,
		selectedOtBadgeTypes,
		isExpanded,
		setIsExpanded,
		sliderStart,
		setSliderStart,
		sliderEnd,
		setSliderEnd,
		filterStaff,
		staff,
		shiftTimeFilters,
		onRoleTypeFilterChange,
		onJobTypeFilterChange,
		onShiftTimeFilterChange,
		onOtBadgeFilterChange,
		onShiftClick,
	};
}
